
import { Building, Group, Cog, Landmark, MapPin, Layers, Box, PawPrint, Zap, Calendar, LayoutGrid } from 'lucide-react';
import type { WorldElementTypeV3 } from './schemas/world-element-v3';

// =============================================================================
// UI TAB TYPE (extends WorldElementTypeV3 with 'all')
// =============================================================================

/**
 * Extended type for the world element tab selector.
 * Includes 'all' for showing every element type at once.
 * This is UI-only and does NOT affect the schema/storage layer.
 */
export type WorldElementTabType = WorldElementTypeV3 | 'all';

// =============================================================================
// V2 WORLD ELEMENT CATEGORIES (existing)
// =============================================================================

export const WORLD_ELEMENT_CATEGORIES = {
  locations: {
    label: 'Locations',
    icon: Building,
  },
  cultures: {
    label: 'Cultures & Societies',
    icon: Group,
  },
  systems: {
    label: 'Systems & Rules',
    icon: Cog,
  },
  history: {
    label: 'History & Events',
    icon: Landmark,
  },
} as const;

// Create a tuple of the category keys for Zod enum validation. This is the single source of truth.
export const WORLD_ELEMENT_CATEGORY_KEYS = Object.keys(WORLD_ELEMENT_CATEGORIES) as [keyof typeof WORLD_ELEMENT_CATEGORIES, ...(keyof typeof WORLD_ELEMENT_CATEGORIES)[]];

// =============================================================================
// V3 WORLD ELEMENT TYPES
// =============================================================================

/**
 * V3 world element types with metadata for UI display.
 * Each type has a label, icon, and description.
 */
export const WORLD_ELEMENT_TYPES_V3 = {
  place: {
    label: 'Place',
    pluralLabel: 'Places',
    icon: MapPin,
    description: 'Locations, buildings, regions',
  },
  set: {
    label: 'Set',
    pluralLabel: 'Sets',
    icon: Layers,
    description: 'Staged environments with sensory detail',
  },
  organization: {
    label: 'Organization',
    pluralLabel: 'Organizations',
    icon: Group,
    description: 'Groups of people with shared purpose',
  },
  object: {
    label: 'Object',
    pluralLabel: 'Objects',
    icon: Box,
    description: 'Artifacts and items',
  },
  species: {
    label: 'Species',
    pluralLabel: 'Species',
    icon: PawPrint,
    description: 'Races, creatures, beings',
  },
  system: {
    label: 'System',
    pluralLabel: 'Systems',
    icon: Zap,
    description: 'Magic systems, technologies, social structures',
  },
  event: {
    label: 'Event',
    pluralLabel: 'Events',
    icon: Calendar,
    description: 'Historical events, prophecies, phenomena',
  },
} as const;

/**
 * Metadata for the "All" tab (UI only).
 */
export const WORLD_ELEMENT_ALL_TAB_CONFIG = {
  label: 'All',
  pluralLabel: 'All Elements',
  icon: LayoutGrid,
  description: 'View all world element types at once',
} as const;

/**
 * V3 world object element subtypes.
 */
export const WORLD_OBJECT_ELEMENTS = ['artifact', 'item', 'clothing'] as const;

/**
 * Metadata for world object element subtypes.
 */
export const WORLD_OBJECT_ELEMENT_METADATA = {
  artifact: {
    label: 'Artifact',
    pluralLabel: 'Artifacts',
    description: 'A specific, unique object with special properties or history (e.g., Excalibur, the One Ring)',
  },
  item: {
    label: 'Item',
    pluralLabel: 'Items',
    description: "A specific, notable object tied to a character or plot, including standalone significant garments whose meaning is independent of any wearer (e.g., the detective's revolver, the crew's Sherman tank, the emperor's cloak as a symbol of rule)",
  },
  clothing: {
    label: 'Outfit',
    pluralLabel: 'Outfits',
    description: "A character's current outfit — the visual look they're wearing. Use `item` instead for standalone significant garments with meaning independent of the wearer.",
  },
} as const;

/**
 * V3 world place element subtypes.
 */
export const WORLD_PLACE_ELEMENTS = ['location', 'building'] as const;

/**
 * Metadata for world place element subtypes.
 */
export const WORLD_PLACE_ELEMENT_METADATA = {
  location: {
    label: 'Location',
    pluralLabel: 'Locations',
    description: 'Geographic areas, regions, territories',
  },
  building: {
    label: 'Building',
    pluralLabel: 'Buildings',
    description: 'Significant structures, landmarks, monuments',
  },
} as const;

/**
 * V3 world set element subtypes.
 */
export const WORLD_SET_ELEMENTS = ['interior', 'exterior'] as const;

/**
 * Metadata for world set element subtypes.
 */
export const WORLD_SET_ELEMENT_METADATA = {
  interior: {
    label: 'Interior',
    pluralLabel: 'Interiors',
    description: 'Indoor staged spaces with sensory detail',
  },
  exterior: {
    label: 'Exterior',
    pluralLabel: 'Exteriors',
    description: 'Outdoor staged spaces with sensory detail',
  },
} as const;

/**
 * V3 world organization element subtypes.
 */
export const WORLD_ORGANIZATION_ELEMENTS = ['faction', 'nation', 'institution'] as const;

/**
 * Metadata for world organization element subtypes.
 */
export const WORLD_ORGANIZATION_ELEMENT_METADATA = {
  faction: {
    label: 'Faction',
    pluralLabel: 'Factions',
    description: 'Secret societies, guilds, informal groups',
  },
  nation: {
    label: 'Nation',
    pluralLabel: 'Nations',
    description: 'Countries, empires, kingdoms, city-states',
  },
  institution: {
    label: 'Institution',
    pluralLabel: 'Institutions',
    description: 'Formal organizations, schools, churches',
  },
} as const;

/**
 * V3 world event element subtypes.
 */
export const WORLD_EVENT_ELEMENTS = ['historical-event', 'prophecy', 'legend'] as const;

/**
 * Metadata for world event element subtypes.
 */
export const WORLD_EVENT_ELEMENT_METADATA = {
  'historical-event': {
    label: 'Historical Event',
    pluralLabel: 'Historical Events',
    description: 'Past occurrences that shaped the world',
  },
  prophecy: {
    label: 'Prophecy',
    pluralLabel: 'Prophecies',
    description: 'Foretold future events',
  },
  legend: {
    label: 'Legend',
    pluralLabel: 'Legends',
    description: 'Mythical or semi-historical stories',
  },
} as const;

/**
 * V3 world species element subtypes.
 */
export const WORLD_SPECIES_ELEMENTS = ['creature', 'race', 'flora'] as const;

/**
 * Metadata for world species element subtypes.
 */
export const WORLD_SPECIES_ELEMENT_METADATA = {
  creature: {
    label: 'Creature',
    pluralLabel: 'Creatures',
    description: 'Animals, monsters, beasts',
  },
  race: {
    label: 'Race',
    pluralLabel: 'Races',
    description: 'Sentient peoples, humanoid races',
  },
  flora: {
    label: 'Flora',
    pluralLabel: 'Flora',
    description: 'Plants, fungi, vegetation',
  },
} as const;

/**
 * V3 world system element subtypes.
 */
export const WORLD_SYSTEM_ELEMENTS = ['magic-system', 'natural-law', 'social-custom', 'technology', 'material'] as const;

/**
 * Metadata for world system element subtypes.
 */
export const WORLD_SYSTEM_ELEMENT_METADATA = {
  'magic-system': {
    label: 'Magic System',
    pluralLabel: 'Magic Systems',
    description: 'Supernatural rules, powers, and costs',
  },
  'natural-law': {
    label: 'Natural Law',
    pluralLabel: 'Natural Laws',
    description: 'Physical or metaphysical rules of the world',
  },
  'social-custom': {
    label: 'Social Custom',
    pluralLabel: 'Social Customs',
    description: 'Cultural norms, taboos, traditions',
  },
  technology: {
    label: 'Technology',
    pluralLabel: 'Technologies',
    description: 'Technological systems and constraints',
  },
  material: {
    label: 'Material',
    pluralLabel: 'Materials',
    description: 'Substances with special properties',
  },
} as const;

// =============================================================================
// V3 DERIVED CONSTANTS (cross-type utilities)
// =============================================================================

/**
 * All world element subtypes across all types, derived from per-type constants.
 * This is the single source of truth used by detection schemas and prompts.
 */
export const ALL_WORLD_ELEMENT_SUBTYPES = [
  ...WORLD_PLACE_ELEMENTS,
  ...WORLD_SET_ELEMENTS,
  ...WORLD_ORGANIZATION_ELEMENTS,
  ...WORLD_OBJECT_ELEMENTS,
  ...WORLD_SPECIES_ELEMENTS,
  ...WORLD_SYSTEM_ELEMENTS,
  ...WORLD_EVENT_ELEMENTS,
] as const;

export type WorldElementSubtype = typeof ALL_WORLD_ELEMENT_SUBTYPES[number];

/**
 * Maps each element subtype to its parent type.
 * Useful for validation and grouping detected elements.
 */
export const ELEMENT_SUBTYPE_TO_TYPE: Record<WorldElementSubtype, WorldElementTypeV3> = {
  location: 'place', building: 'place',
  interior: 'set', exterior: 'set',
  faction: 'organization', nation: 'organization', institution: 'organization',
  artifact: 'object', item: 'object', clothing: 'object',
  creature: 'species', race: 'species', flora: 'species',
  'magic-system': 'system', 'natural-law': 'system', 'social-custom': 'system', technology: 'system', material: 'system',
  'historical-event': 'event', prophecy: 'event', legend: 'event',
};